import React, { useState } from "react";
import { Send, CheckCircle2, Loader2 } from "lucide-react";
import { BUSINESS, CITIES } from "@/constants/business";
import { trackLead } from "@/utils/analytics";
import { submitLead } from "@/utils/leads";

export default function ContactForm({ sourcePage = "home", defaultCity = "" }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    city: defaultCity,
    service_interest: "Passenger Elevator",
    message: "",
    whatsapp_optin: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const update = (k) => (e) =>
    setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.phone.trim()) {
      setError("Please provide your name and phone number.");
      return;
    }
    setSubmitting(true);
    try {
      await submitLead({ ...form, source_page: sourcePage });
      trackLead({ source: sourcePage, service: form.service_interest, city: form.city });
      setSuccess(true);
      setForm({
        name: "",
        email: "",
        phone: "",
        city: defaultCity,
        service_interest: "Passenger Elevator",
        message: "",
        whatsapp_optin: true,
      });
    } catch (err) {
      setError("Something went wrong. Please call us at " + BUSINESS.phone);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div
        data-testid="contact-success"
        className="border border-[#b08d57]/40 bg-[#b08d57]/5 p-10 flex flex-col items-center text-center"
      >
        <CheckCircle2 className="w-14 h-14 text-[#b08d57] mb-4" />
        <h3 className="font-display text-2xl font-bold text-[#1a1a1a] mb-2">Thank you!</h3>
        <p className="text-[#6b6b6b] max-w-md">
          Our team will reach out within one business day. For urgent requirements, call us on{" "}
          <a href={`tel:${BUSINESS.phoneRaw}`} className="text-[#b08d57] font-semibold">
            {BUSINESS.phone}
          </a>
          .
        </p>
        <button
          onClick={() => setSuccess(false)}
          data-testid="contact-send-another"
          className="mt-6 text-xs uppercase tracking-[0.2em] text-[#6b6b6b] hover:text-[#b08d57]"
        >
          Send another enquiry
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field
          label="Your Name *"
          testid="contact-name"
          value={form.name}
          onChange={update("name")}
        />
        <Field
          label="Phone Number *"
          testid="contact-phone"
          type="tel"
          value={form.phone}
          onChange={update("phone")}
        />
        <Field
          label="Email"
          testid="contact-email"
          type="email"
          value={form.email}
          onChange={update("email")}
        />
        <div>
          <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6b6b6b] mb-2 block">
            City
          </label>
          <select
            data-testid="contact-city"
            value={form.city}
            onChange={update("city")}
            className="w-full bg-[#faf7f2] border border-[#e5ddd0] focus:border-[#b08d57] outline-none px-4 py-3 text-[#1a1a1a] text-sm"
          >
            <option value="">Select your city</option>
            {CITIES.map((c) => (
              <option key={c.slug} value={c.name}>{`${c.name}, ${c.state}`}</option>
            ))}
            <option value="Other">Other</option>
          </select>
        </div>
      </div>

      <div>
        <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6b6b6b] mb-2 block">
          I'm interested in
        </label>
        <select
          data-testid="contact-service"
          value={form.service_interest}
          onChange={update("service_interest")}
          className="w-full bg-[#faf7f2] border border-[#e5ddd0] focus:border-[#b08d57] outline-none px-4 py-3 text-[#1a1a1a] text-sm"
        >
          <option>Passenger Elevator</option>
          <option>Home Lift</option>
          <option>Freight / Cargo Elevator</option>
          <option>Hospital Lift</option>
          <option>Escalator</option>
          <option>Moving Walkway</option>
          <option>Maintenance / AMC</option>
          <option>Modernization & Repair</option>
          <option>Other</option>
        </select>
      </div>

      <div>
        <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6b6b6b] mb-2 block">
          Message
        </label>
        <textarea
          data-testid="contact-message"
          value={form.message}
          onChange={update("message")}
          rows={4}
          placeholder="Tell us about your building, number of floors, capacity requirements…"
          className="w-full bg-[#faf7f2] border border-[#e5ddd0] focus:border-[#b08d57] outline-none px-4 py-3 text-[#1a1a1a] text-sm resize-none"
        />
      </div>

      <label className="flex items-start gap-3 text-sm text-[#6b6b6b] cursor-pointer">
        <input
          type="checkbox"
          data-testid="contact-whatsapp-optin"
          checked={form.whatsapp_optin}
          onChange={update("whatsapp_optin")}
          className="mt-1 accent-[#b08d57]"
        />
        <span>Send updates via WhatsApp — we'll only message you about your enquiry.</span>
      </label>

      {error && (
        <div data-testid="contact-error" className="text-red-400 text-sm border border-red-500/30 bg-red-500/10 px-4 py-3">
          {error}
        </div>
      )}

      <button
        type="submit"
        data-testid="contact-submit"
        disabled={submitting}
        className="w-full sm:w-auto bg-[#b08d57] hover:bg-[#8a6a3d] disabled:opacity-60 text-white px-8 py-4 font-semibold uppercase tracking-wider flex items-center justify-center gap-3 transition-colors"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        {submitting ? "Sending…" : "Request Quote"}
      </button>
    </form>
  );
}

function Field({ label, testid, ...props }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6b6b6b] mb-2 block">
        {label}
      </label>
      <input
        {...props}
        data-testid={testid}
        className="w-full bg-[#faf7f2] border border-[#e5ddd0] focus:border-[#b08d57] outline-none px-4 py-3 text-[#1a1a1a] text-sm"
      />
    </div>
  );
}
